import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import { apiService } from '../services/api';
import { Skel } from './Skeleton';

// Red facts for the Historical Health issues bar — mirrors exactly what the
// card itself alarms on (DATA ISSUES band + close-mismatch row), so bar and
// card can never disagree. Warnings stay out: watch, don't act.
export const healthIssues = (stocks) => {
  const issues = [];
  for (const [symbol, d] of Object.entries(stocks || {})) {
    const parts = [];
    if (d.missing_days > 0) parts.push(`${d.missing_days} missing day${d.missing_days === 1 ? '' : 's'}`);
    if (d.critical_days > 0) parts.push(`${d.critical_days} critical day${d.critical_days === 1 ? '' : 's'}`);
    if (d.close_mismatch_days > 0) parts.push(`${d.close_mismatch_days} close mismatch${d.close_mismatch_days === 1 ? '' : 'es'}`);
    if (!parts.length && d.overall_health === 'critical') parts.push('critical');
    if (parts.length) issues.push({ id: `health-${symbol}`, label: symbol, reason: parts.join(', ') });
  }
  return issues;
};

const MARKET_CONFIG = {
  india: { title: 'India Indexes', icon: 'IN', iconColors: 'from-orange-500 to-yellow-500', gridCols: 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3' },
  us: { title: 'US Indexes', icon: 'US', iconColors: 'from-blue-500 to-purple-600', gridCols: 'grid-cols-1 lg:grid-cols-2' },
  asia_pacific: { title: 'Asia-Pacific Indexes', icon: 'AP', iconColors: 'from-red-500 to-pink-600', gridCols: 'grid-cols-1 lg:grid-cols-2' },
  europe: { title: 'Europe Indexes', icon: 'EU', iconColors: 'from-indigo-500 to-blue-600', gridCols: 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3' },
  crypto: { title: 'Crypto', icon: '₿', iconColors: 'from-yellow-500 to-orange-500', gridCols: 'grid-cols-1 lg:grid-cols-2' },
  precious_metals: { title: 'Precious Metals', icon: 'Au', iconColors: 'from-yellow-600 to-amber-500', gridCols: 'grid-cols-1 lg:grid-cols-2' },
};

const SYMBOL_ICONS = {
  SENSEX: 'SX', NIFTY50: 'N50', NIFTYBANK: 'BNK', NIFTYIT: 'IT',
  NIFTYMIDCAP100: 'MID', NIFTYSMALLCAP100: 'SML', NIFTYPHARMA: 'PHR',
  NIFTYENERGY: 'ENR', NIFTYAUTO: 'AUT',
  SNP: 'S&P', IXIC: 'NDQ', DJI: 'DOW', RUT: 'RUT',
  HSI: 'HSI', N225: 'N225', DAX: 'DAX', FTSE: 'FTSE', CAC40: 'CAC', IMOEX: 'MOX',
  BTC: 'BTC', ETH: 'ETH', GOLD: 'Au', SILVER: 'Ag',
};

const SYMBOL_COLORS = {
  SENSEX: 'from-blue-500 to-purple-600', NIFTY50: 'from-blue-400 to-indigo-600',
  NIFTYBANK: 'from-green-500 to-teal-600', NIFTYIT: 'from-purple-500 to-pink-600',
  NIFTYMIDCAP100: 'from-cyan-500 to-blue-600', NIFTYSMALLCAP100: 'from-amber-500 to-orange-600',
  SNP: 'from-blue-500 to-purple-600', IXIC: 'from-green-500 to-emerald-600',
  DJI: 'from-indigo-500 to-blue-600', RUT: 'from-violet-500 to-purple-600',
  HSI: 'from-red-500 to-rose-600', N225: 'from-pink-500 to-red-600',
  DAX: 'from-yellow-500 to-amber-600', FTSE: 'from-sky-500 to-blue-600',
  CAC40: 'from-indigo-500 to-violet-600', IMOEX: 'from-red-600 to-orange-600',
  BTC: 'from-yellow-500 to-orange-500', ETH: 'from-blue-400 to-purple-500',
  GOLD: 'from-yellow-600 to-amber-500', SILVER: 'from-gray-400 to-slate-500',
};

// A single backend request never exceeds this window (the proven-safe envelope).
// Longer periods (60/90D) are fetched as several of these windows in parallel.
const HEALTH_WINDOW = 30;

// Split a period into <=30-day windows: 90 -> [{days:30,offset:0},{30,30},{30,60}].
const buildHealthWindows = (period) => {
  const windows = [];
  let offset = 0;
  while (offset < period) {
    const days = Math.min(HEALTH_WINDOW, period - offset);
    windows.push({ days, offset });
    offset += days;
  }
  return windows;
};

const HEALTH_RANK = { healthy: 0, warning: 1, critical: 2 };
const HEALTH_SUM_KEYS = [
  'days_analyzed', 'total_records', 'expected_records', 'missing_days',
  'healthy_days', 'warning_days', 'critical_days', 'pending_days',
  'holiday_days', 'close_mismatch_days',
];

// Merge two per-symbol health blocks from adjacent (disjoint) date windows.
// Per-day counts are summed; overall health takes the worst of the two.
const mergeHealthStock = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  const merged = { ...a };
  HEALTH_SUM_KEYS.forEach((k) => { merged[k] = (a[k] || 0) + (b[k] || 0); });
  merged.daily_status = [...(a.daily_status || []), ...(b.daily_status || [])];
  merged.null_columns = { ...(a.null_columns || {}) };
  Object.entries(b.null_columns || {}).forEach(([k, v]) => {
    merged.null_columns[k] = (merged.null_columns[k] || 0) + v;
  });
  merged.overall_health =
    (HEALTH_RANK[b.overall_health] || 0) > (HEALTH_RANK[a.overall_health] || 0)
      ? b.overall_health : a.overall_health;
  merged.latest_update =
    [a.latest_update, b.latest_update].filter(Boolean).sort().pop() || null;
  merged.data_completeness_percentage =
    Math.round((merged.healthy_days / Math.max(1, merged.expected_records)) * 1000) / 10;
  return merged;
};

const Legend = () => (
  <div className="flex flex-wrap gap-3 mb-4 text-[10px] text-dark-400">
    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-500 inline-block"></span>Healthy</span>
    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-yellow-500 inline-block"></span>Warning</span>
    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500 inline-block"></span>Critical</span>
    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-700 inline-block"></span>Missing</span>
    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-500 inline-block"></span>Close Mismatch</span>
    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500/60 inline-block"></span>Holiday</span>
    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-500 inline-block"></span>Pending</span>
  </div>
);

// how many index cards each market showed last visit — the loading skeleton's shape
const CARDS_KEY = 'historicalHealth.cards.v1';
const rememberedCards = () => { try { return JSON.parse(localStorage.getItem(CARDS_KEY) || '{}'); } catch { return {}; } };

// One index card still loading: its real shape and labels, figures as shimmer
// (Apal 2026-11-03: "anything that's loading must give loading effect").
const HealthCardSkeleton = ({ period }) => {
  const rows = Math.max(1, Math.ceil(period / 20));
  return (
    <div className="border rounded-lg p-5 bg-gradient-to-br from-dark-800/50 to-dark-900/50 border-dark-700" data-testid="health-card-skeleton">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Skel className="w-12 h-12" rounded="rounded-full" />
          <div>
            <div className="h-7 flex items-center"><Skel className="h-4 w-28" /></div>
            <div className="h-5 flex items-center"><Skel className="h-3 w-32" /></div>
          </div>
        </div>
        <Skel className="h-6 w-16" />
      </div>
      <div className="grid grid-cols-4 gap-1.5 mb-4">
        {['Healthy', 'Warning', 'Missing', 'Holidays'].map(l => (
          <div key={l} className="text-center bg-dark-800 rounded-lg p-2">
            <div className="h-7 flex items-center justify-center"><Skel className="h-4 w-6" /></div>
            <p className="text-[10px] text-dark-400">{l}</p>
          </div>
        ))}
      </div>
      <div className="bg-dark-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-white">Daily Status</p>
          <span className="text-xs text-dark-400">{period}d</span>
        </div>
        <div className="mb-3 space-y-0.5">
          {Array.from({ length: rows }, (_, i) => <Skel key={i} className="h-7 w-full" />)}
        </div>
        <div className="flex items-center justify-between text-xs text-dark-400"><span>Oldest</span><span>Latest</span></div>
      </div>
    </div>
  );
};

const StockHealthMonitor = ({ market, period = 30, onIssues, flashKey }) => {
  // Period is controlled by the parent's single global selector so all market
  // sections stay in sync (one click sets the timeline for every section).
  const selectedPeriod = period;
  const [stocksHealth, setStocksHealth] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  // Partial-failure banner: { failed, total } when some (but not all) windows fail.
  const [windowError, setWindowError] = useState(null);
  // Bumped by Retry buttons to re-run the fetch.
  const [reloadNonce, setReloadNonce] = useState(0);
  // Latest onIssues via a ref so the parent's callback identity never
  // re-triggers the fetch effect.
  const onIssuesRef = useRef(onIssues);
  onIssuesRef.current = onIssues;

  const config = MARKET_CONFIG[market];

  useEffect(() => {
    if (!config) return;
    let cancelled = false;
    const abort = new AbortController();   // a newer period (or leaving the page) cancels these
    const fetchHealth = async () => {
      try {
        setIsLoading(true);
        setWindowError(null);
        // null = "still checking" — the issues bar must never claim green
        // for a market whose verdict hasn't loaded yet.
        onIssuesRef.current?.(market, null);
        // Fetch the period as one or more <=30-day windows in parallel, then
        // merge. Each request stays inside the proven-safe envelope, so no
        // single request can time out regardless of the selected period.
        const windows = buildHealthWindows(selectedPeriod);
        const results = await Promise.allSettled(
          windows.map((w) => apiService.getMarketHealth({ market, days: w.days, offset: w.offset }, { signal: abort.signal }))
        );
        if (cancelled) return;

        const ok = results
          .filter((r) => r.status === 'fulfilled' && r.value?.data && !r.value.data.error)
          .map((r) => r.value.data);
        const failed = results.length - ok.length;

        if (ok.length === 0) {
          // Total failure — surface a backend error if one came back, else a
          // generic message. Never leave the panel blank on failure.
          const errored = results.find((r) => r.status === 'fulfilled' && r.value?.data?.error);
          setStocksHealth(
            errored
              ? errored.value.data
              : { error: `Couldn't load the ${selectedPeriod}-day health data. Please retry.` }
          );
          // The section shows its own error + Retry; report "no verdicts"
          // (empty) so the bar isn't stuck on scanning forever.
          onIssuesRef.current?.(market, []);
          return;
        }

        const merged = {};
        ok.forEach((resp) => {
          Object.entries(resp.stocks_health || {}).forEach(([sym, data]) => {
            merged[sym] = mergeHealthStock(merged[sym], data);
          });
        });
        // Timeline must stay oldest -> newest (the render slices from the end).
        Object.values(merged).forEach((s) => {
          s.daily_status = (s.daily_status || []).sort((x, y) =>
            x.date < y.date ? -1 : x.date > y.date ? 1 : 0
          );
        });

        // India symbols whose NSE/BSE fetch didn't finish inside any window's wait —
        // drives a clear banner instead of silently missing exchange verification.
        const exchangePending = [...new Set(ok.flatMap((resp) => resp.exchange_pending || []))].sort();
        setStocksHealth({
          stocks_health: merged,
          analysis_period: `${selectedPeriod} days`,
          exchange_pending: exchangePending,
          no_active_symbols: ok.length > 0 && ok.every((r) => r.no_active_symbols),
        });
        // Partial failure: we have SOME windows but not all — show the data we
        // have AND warn that part of the range is missing (don't hide it).
        setWindowError(failed > 0 ? { failed, total: results.length } : null);
        try {
          const n = ok.every((r) => r.no_active_symbols) ? 0 : Object.keys(merged).length;
          localStorage.setItem(CARDS_KEY, JSON.stringify({ ...rememberedCards(), [market]: n }));
        } catch { /* no storage: the skeleton uses two cards */ }
        onIssuesRef.current?.(market, healthIssues(merged));
      } catch (e) {
        if (!cancelled) {
          setStocksHealth({ error: 'Failed to load health data. Please retry.' });
          onIssuesRef.current?.(market, []);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchHealth();
    return () => { cancelled = true; abort.abort(); };
  }, [selectedPeriod, market, reloadNonce]);

  if (!config) return null;
  if (!isLoading && !stocksHealth) return null;

  const getStatusColor = (s) => {
    switch (s) {
      case 'healthy': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      case 'missing': return 'bg-red-700';
      case 'close_mismatch': return 'bg-orange-500';
      case 'holiday': return 'bg-blue-500/60';
      case 'pending': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  if (isLoading) {
    // a market that had no indexes last visit stays out of the way
    const cards = rememberedCards()[market];
    if (cards === 0) return null;
    return (
      <div className="dashboard-card" data-testid={`health-loading-${market}`}>
        {/* the page's ONE strip ("Checking all markets", the issues bar) says it is
            loading — a strip per market panel was six at once; each panel shows
            its own shape */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${config.iconColors} flex items-center justify-center text-white text-[10px] font-bold`}>{config.icon}</div>
            <h3 className="text-xl font-semibold text-white">{config.title}</h3>
            <Skel className="h-5 w-8" />
          </div>
          <span className="text-xs text-dark-400 bg-dark-700/60 rounded-md px-2 py-1">{selectedPeriod}-day window</span>
        </div>
        <Legend />
        <div className={`grid ${config.gridCols} gap-6`}>
          {Array.from({ length: cards || 2 }, (_, i) => <HealthCardSkeleton key={i} period={selectedPeriod} />)}
        </div>
      </div>
    );
  }

  if (stocksHealth.error) {
    return (
      <div className="dashboard-card">
        <h3 className="text-xl font-semibold text-white mb-4">{config.title} - ERROR</h3>
        <div className="flex items-center justify-between gap-3 text-red-400">
          <p>Error: {stocksHealth.error}</p>
          <button onClick={() => setReloadNonce((n) => n + 1)}
            className="shrink-0 rounded-md bg-dark-700 hover:bg-dark-600 px-3 py-1 text-xs font-medium text-white transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const stocks = stocksHealth.stocks_health || {};
  const stockCount = Object.keys(stocks).length;

  // A market we don't run yet (no active symbols) renders nothing at all —
  // an empty placeholder card is noise, not information.
  if (stockCount === 0 && stocksHealth.no_active_symbols) return null;

  if (stockCount === 0) {
    return (
      <div className="dashboard-card">
        <h3 className="text-xl font-semibold text-white mb-4">{config.title} - No Data</h3>
        <div className="text-dark-400"><p>No active index data found for this market</p></div>
      </div>
    );
  }

  return (
    <div className="dashboard-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${config.iconColors} flex items-center justify-center text-white text-[10px] font-bold`}>{config.icon}</div>
          <h3 className="text-xl font-semibold text-white">{config.title} ({stockCount})</h3>
        </div>
        <span className="text-xs text-dark-400 bg-dark-700/60 rounded-md px-2 py-1">{selectedPeriod}-day window</span>
      </div>

      {/* Partial-failure warning — some windows loaded, some didn't */}
      {windowError && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-600/50 bg-amber-900/30 px-4 py-2.5">
          <p className="text-xs text-amber-300">
            ⚠ Showing partial data — {windowError.failed} of {windowError.total} time windows failed to load.
            Some days in this {selectedPeriod}-day range may be missing.
          </p>
          <button onClick={() => setReloadNonce((n) => n + 1)}
            className="shrink-0 rounded-md bg-amber-700/60 hover:bg-amber-600 px-3 py-1 text-xs font-medium text-white transition-colors">
            Retry
          </button>
        </div>
      )}

      {/* NSE/BSE fetch still running — say so clearly instead of silently showing
          aggregator-only verification. One reload after a few seconds resolves it. */}
      {stocksHealth.exchange_pending?.length > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-sky-600/50 bg-sky-900/30 px-4 py-2.5">
          <p className="text-xs text-sky-300">
            Fetching official NSE/BSE closes for {stocksHealth.exchange_pending.join(', ')} — the first
            load takes a few seconds. Days show EODHD/Yahoo verification meanwhile; reload shortly to
            see exchange verification.
          </p>
          <button onClick={() => setReloadNonce((n) => n + 1)}
            className="shrink-0 rounded-md bg-sky-700/60 hover:bg-sky-600 px-3 py-1 text-xs font-medium text-white transition-colors">
            Reload
          </button>
        </div>
      )}

      {/* Legend */}
      <Legend />

      {/* Cards Grid */}
      <div className={`grid ${config.gridCols} gap-6 cb-fade-in`}>
        {Object.entries(stocks).map(([symbol, data]) => {
          const hasCritical = data.overall_health === 'critical' || data.critical_days > 0 || data.missing_days > 0;
          const hasCloseMismatch = data.close_mismatch_days > 0;
          const iconLabel = SYMBOL_ICONS[symbol] || symbol.substring(0, 3);
          const iconColor = SYMBOL_COLORS[symbol] || config.iconColors;

          return (
            <div key={symbol} id={`health-${symbol}`} className={`border rounded-lg p-5 bg-gradient-to-br from-dark-800/50 to-dark-900/50 transition-all hover:from-dark-700/50 hover:to-dark-800/50 ${hasCritical ? 'border-red-500/50 shadow-lg shadow-red-500/10' : hasCloseMismatch ? 'border-orange-500/50' : 'border-dark-700'}${
              flashKey === `health-${symbol}` ? ' ring-2 ring-red-400/80' : ''}`}>

              {hasCritical && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-800/50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400 font-semibold text-sm">DATA ISSUES DETECTED</span>
                  </div>
                  <p className="text-red-300 text-xs mt-1">
                    {data.critical_days > 0 && `${data.critical_days} critical, `}
                    {data.missing_days > 0 && `${data.missing_days} missing`}
                    {hasCloseMismatch && `, ${data.close_mismatch_days} close mismatch`}
                  </p>
                </div>
              )}

              {/* Card Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${iconColor} flex items-center justify-center font-bold text-white text-xs ${hasCritical ? 'ring-2 ring-red-500/50' : ''}`}>{iconLabel}</div>
                  <div>
                    <h4 className="text-lg font-semibold text-white">{data.symbol_name}</h4>
                    <p className="text-dark-400 text-sm">{data.healthy_days}/{data.expected_records || data.days_analyzed} verified days</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${data.overall_health === 'healthy' ? 'bg-green-400/10 text-green-400' : data.overall_health === 'warning' ? 'bg-yellow-400/10 text-yellow-400' : 'bg-red-400/10 text-red-400'}`}>{data.overall_health}</span>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-1.5 mb-4">
                <div className="text-center bg-dark-800 rounded-lg p-2"><p className="text-lg font-bold text-green-400">{data.healthy_days}</p><p className="text-[10px] text-dark-400">Healthy</p></div>
                <div className="text-center bg-dark-800 rounded-lg p-2"><p className="text-lg font-bold text-yellow-400">{data.warning_days}</p><p className="text-[10px] text-dark-400">Warning</p></div>
                <div className={`text-center rounded-lg p-2 ${data.missing_days > 0 ? 'bg-red-900/40 ring-1 ring-red-500' : 'bg-dark-800'}`}><p className={`text-lg font-bold ${data.missing_days > 0 ? 'text-red-300' : 'text-red-600'}`}>{data.missing_days}</p><p className="text-[10px] text-dark-400">Missing</p></div>
                <div className="text-center bg-dark-800 rounded-lg p-2"><p className="text-lg font-bold text-blue-400">{data.holiday_days || 0}</p><p className="text-[10px] text-dark-400">Holidays</p></div>
              </div>

              {/* Close Mismatch Row */}
              {hasCloseMismatch && (
                <div className="mb-4 px-3 py-2 bg-orange-900/20 border border-orange-700/30 rounded-lg">
                  <span className="text-orange-400 text-xs font-medium">
                    {data.close_mismatch_days} day{data.close_mismatch_days > 1 ? 's' : ''} with close price mismatch
                  </span>
                </div>
              )}

              {/* Daily Timeline */}
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-white">Daily Status</p>
                  <span className="text-xs text-dark-400">{selectedPeriod}d</span>
                </div>
                <div className="flex justify-center mb-3">
                  <div className="flex gap-0.5 flex-wrap max-w-full">
                    {data.daily_status?.slice(-selectedPeriod).map((day, index) => {
                      const date = new Date(day.date + 'T00:00:00');
                      const humanDate = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                      const statusLabels = { healthy: 'Healthy', warning: 'Warning', critical: 'Critical', missing: 'Missing Data', close_mismatch: 'Close Mismatch', holiday: 'Holiday / Weekend', pending: 'Pending' };

                      return (
                        <div
                          key={index}
                          className={`w-3.5 h-7 rounded-sm flex-shrink-0 ${getStatusColor(day.status)} cursor-pointer hover:opacity-80 transition-opacity`}
                          onMouseEnter={(e) => {
                            const rect = e.target.getBoundingClientRect();
                            setTooltip({
                              show: true,
                              content: [humanDate, statusLabels[day.status] || day.status,
                                // Verified sources. The NSE/BSE note is a schedule fact, never a
                                // warning: the exchange publishes its official close ~9:30 PM IST.
                                ...(() => {
                                  const src = day.close_verification_pending;
                                  const note = src
                                    ? (day.close_verification_pending_reason === 'awaiting_publication'
                                      ? `${src} verifies after 9:30 PM IST`
                                      : `${src} check loading…`)
                                    : null;
                                  if (day.close_verified_by) return [`Close verified: ${day.close_verified_by}${note ? ` (${note})` : ''}`];
                                  return note ? [`Close verification: ${note}`] : [];
                                })(),
                                ...(day.issues?.length > 0 && day.status !== 'holiday' && day.status !== 'pending' ? [`Issues: ${day.issues.join(', ')}`] : [])],
                              x: rect.left + rect.width / 2,
                              y: rect.top - 10
                            });
                          }}
                          onMouseLeave={() => setTooltip({ show: false, content: null, x: 0, y: 0 })}
                          style={{ minWidth: '14px', minHeight: '28px' }}
                        ></div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-dark-400"><span>Oldest</span><span>Latest</span></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tooltip */}
      {tooltip.show && tooltip.content && (
        <div className="fixed z-50 bg-dark-900 border border-dark-600 rounded-lg px-3.5 py-2.5 shadow-xl pointer-events-none max-w-xs"
          style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px`, transform: 'translateX(-50%) translateY(-100%)' }}>
          <div className="space-y-0.5">
            {tooltip.content.map((line, i) => (
              <div key={i} className={i === 0 ? 'text-xs font-semibold text-white' : i === 1 ? 'text-xs font-medium text-dark-200' : 'text-[11px] text-dark-400'}>{line}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StockHealthMonitor;
